import type { ComponentProps, ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { Fragment } from 'react';

import { Menu, Transition } from '@headlessui/react';

import { classNames } from '@/utils';

//Components
import Icon from '@/components/base/Icon';

//export this to have access to const in test. If changes are made the test will still be accurate.
export const BASE = 'base';
export const NO_BACKGROUND = 'no_background';
export const NO_BACKGROUND_BLACK = 'no_background_black';
const CENTER = 'center';
const CENTER_LEFT = 'center-left';
const TOP = 'top';
const LEFT = 'left';
const SMALL = 'sm';
const LARGE = 'lg';
const FULL = 'full';
const CUSTOM = 'custom';

//export this to have access to the object in the test. If changes are made the test will still be accurate.
export const DropdownSize = {
    [SMALL]: 'w-32',
    [BASE]: 'w-56',
    [LARGE]: 'w-72',
    [FULL]: 'min-w-max w-full',
    [CUSTOM]: ''
} as const;

export const DropdownPosition = {
    [BASE]: 'right-0',
    [LEFT]: 'left-0',
    [CENTER]: 'right-1/2 translate-x-1/2',
    [CENTER_LEFT]: 'left-9 -top-10',
    [TOP]: 'right-0 -top-3 -translate-y-full'
} as const;

const DropdownButton = {
    [BASE]: 'capitalize bg-white px-3 py-2 text-xs font-GSemiBold text-icoGray-500 hover:cursor-pointer',
    [NO_BACKGROUND]: 'py-2 text-xs font-GMedium hover:cursor-pointer',
    [NO_BACKGROUND_BLACK]: 'text-sm text-black hover:cursor-pointer'
} as const;

const DropdownBorder = 'border border-icoGray-300 dark:border-icoDarkMode-beton';

const BaseButton =
    'flex justify-between items-center rounded-md space-x-2 w-full focus:outline-none focus:ring-2 focus:ring-icoBlue-200 dark:ring-offset-icoDarkMode-deepBlack';

export type DropdownProps = {
    id?: string;
    button: ReactNode;
    children: ReactNode;
    size?: keyof typeof DropdownSize;
    position?: keyof typeof DropdownPosition;
    hasBorder?: boolean;
    classNameWrapper?: string;
    className?: string;
};
const Dropdown = ({
    id,
    button,
    children,
    size = 'base',
    position = 'base',
    hasBorder = false,
    classNameWrapper,
    className
}: DropdownProps) => {
    return (
        <div id={id} className={classNames(classNameWrapper)} data-testid="dropdown-wrapper">
            <Menu as="div" className="relative inline-block size-full text-left">
                {({ open }) => (
                    <>
                        {button}

                        <Transition
                            as={Fragment}
                            show={open}
                            enter="transition duration-100 ease-out"
                            enterFrom="scale-95 opacity-0"
                            enterTo="scale-100 opacity-100"
                            leave="transition duration-75 ease-out"
                            leaveFrom="scale-100 opacity-100"
                            leaveTo="scale-95 opacity-0">
                            <Menu.Items
                                static
                                className={classNames(
                                    'absolute z-10 mt-2 origin-top-right rounded-md bg-white text-xs text-icoGray-500 shadow-icoM focus:outline-none dark:bg-icoDarkMode-anthracite',
                                    DropdownPosition[position] ?? DropdownPosition[BASE],
                                    DropdownSize[size] ?? DropdownSize[BASE],
                                    hasBorder && DropdownBorder,
                                    className
                                )}>
                                {children}
                            </Menu.Items>
                        </Transition>
                    </>
                )}
            </Menu>
        </div>
    );
};

const Items = ({ ...restProps }: ComponentProps<typeof Menu.Items>) => {
    return <Menu.Items {...restProps} />;
};

Dropdown.Items = Items;

const baseItem = 'px-1 py-1 hover:cursor-pointer';

type ItemProps = ComponentPropsWithoutRef<(typeof Menu)['Item']> & { className?: string };
const Item = ({ as = 'div', className, ...restProps }: ItemProps) => (
    <Menu.Item as={as} className={className ?? baseItem} {...restProps} />
);

Dropdown.Item = Item;

export type DropdownBaseButtonProps = ComponentPropsWithoutRef<'button'> & {
    buttonStyle?: keyof typeof DropdownButton;
};
const DropdownBaseButton = ({
    children,
    buttonStyle = 'base',
    disabled
}: DropdownBaseButtonProps) => (
    <Menu.Button
        data-testid="dropdown-button"
        className={classNames(BaseButton, DropdownButton[buttonStyle], {
            'pointer-events-none bg-icoGray-300 text-icoGray-400': disabled,
            'dark:bg-icoDarkMode-anthracite dark:text-white': !disabled
        })}
        disabled={disabled}>
        {children}
        <Icon
            icon="chevron-bottom"
            className={classNames('dark:text-icoDarkMode-wolf', {
                'text-icoGray-400': disabled,
                'text-icoGray-500': !disabled && buttonStyle !== NO_BACKGROUND_BLACK,
                'h-3 text-13': buttonStyle !== NO_BACKGROUND_BLACK,
                'h-4 text-lg': buttonStyle === NO_BACKGROUND_BLACK
            })}
        />
    </Menu.Button>
);

Dropdown.BaseButton = DropdownBaseButton;

Dropdown.Button = Menu.Button;

type DividerProps = ComponentPropsWithoutRef<'div'>;
const Divider = ({ children, className, ...restProps }: DividerProps) => (
    <div
        className={`${className ?? 'divide-y divide-gray-100 dark:divide-icoDarkMode-beton'}`}
        {...restProps}>
        {children}
    </div>
);

Dropdown.Divider = Divider;

type BaseItemProps = ComponentPropsWithoutRef<'p'> & {
    as?: ElementType;
    active: boolean;
    disabled?: boolean;
};
export const BaseItem = ({
    as: Component = 'p',
    children,
    active,
    className,
    ...restProps
}: BaseItemProps) => (
    <Component
        data-testid="dropdown-base-item"
        className={classNames(
            [
                'block w-full rounded-md px-2 py-2 text-left font-GSemiBold transition duration-200 dark:text-white',
                'hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoBlue/20'
            ],
            { 'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20': active },
            className
        )}
        {...restProps}>
        {children}
    </Component>
);

export default Dropdown;
